import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {catchError, map, Observable} from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import { Address, Addresses } from './address';
import { ConfigService } from '@docma-mm-core/config/config.service';
import {StructureAddress} from '@docma-mm-core/address/structure-address';
import {constants} from '@docma-mm-core/constants';

@Injectable()
export class AddressService extends BasedataService {

  private projectUrl = '/projects/';
  private addressUrl = '/addresses';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getProjectAddressUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.addressUrl);
  }

  // get project addresses list
  getProjectAddresses(projectId: number, top: number = 10000, skip: number = 0): Observable<Addresses> {
    const endpoint = this.getProjectAddressUrl(projectId);
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    return this.http
      .get<Addresses>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  getProjectStructureAddresses(projectId: number): Observable<StructureAddress> {
    const endpoint =  super.getFullUrl(this.projectUrl + projectId + this.addressUrl + '/info');
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', `${constants.MaxInt}`); }
    return this.http
      .get<any>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(map(m => m.Data), catchError(e  => this.handleError(e)));
  }

  // get project address by id
  getProjectAddressById(addressId: number, projectId: number): Observable<Address> {
    const endpoint = this.getProjectAddressUrl(projectId) + '/' + addressId;
    return this.http
      .get<Address>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new address
  createProjectAddress(projectId: number, address: Address): Observable<Address> {
    const endpoint = this.getProjectAddressUrl(projectId);
    return this.http
      .post<Address>(endpoint, JSON.stringify(address), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing address
  updateProjectAddress(addressId: number, projectId: number, address: Address): Observable<Address> {
    const endpoint = this.getProjectAddressUrl(projectId) + '/' + addressId;
    return this.http
      .put<Address>(endpoint, JSON.stringify(addressId), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

   // delete address
  deleteProjectAddress(addressId: number, projectId: number): Observable<string> {
    const endpoint = this.getProjectAddressUrl(projectId) + '/' + addressId;
    return this.http
      .delete<string>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // assign project address
  assignProjectAddress(addressId: number, projectId: number, structureId: number, type: number = 0): Observable<string> {
    const endpoint = this.getProjectAddressUrl(projectId) + '/' + addressId + '/assign/' + structureId;
    let httpParams = new HttpParams();
    httpParams = httpParams.set('type', type.toString());
    return this.http
      .put<string>(endpoint, null, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // remove project address assignment
  removeProjectAddressAssignment(addressId: number, projectId: number, structureId: number, type: number = 0): Observable<any> {
    const endpoint = this.getProjectAddressUrl(projectId) + '/' + addressId + '/assign/' + structureId;
    let httpParams = new HttpParams();
    httpParams = httpParams.set('type', type.toString());
    return this.http
      .delete<any>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

}
